import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Icon, type IconName } from '../ui/Icon';

export interface MenuItem {
  id: string;
  label: string;
  onSelect: () => void;
  icon?: IconName;
  /** Present for a checkable item (role menuitemcheckbox). */
  checked?: boolean;
  disabled?: boolean;
  hint?: string;
  testId?: string;
}

/**
 * Small popover menu (WAI-ARIA menu button): Enter/Space/ArrowDown open it on the first item,
 * arrows and Home/End move, Escape closes and gives focus back, Tab or a click outside close it.
 */
export function Menu({
  label,
  items,
  children,
  align = 'right',
  className = 'btn-quiet',
  testId,
  showLabel = false,
  icon = 'more',
}: {
  label: string;
  items: MenuItem[];
  /** Custom button content (defaults to the icon, plus the label when showLabel). */
  children?: ReactNode;
  align?: 'left' | 'right';
  className?: string;
  testId?: string;
  showLabel?: boolean;
  icon?: IconName;
}) {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!list.current?.contains(target) && !button.current?.contains(target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const focusItem = (i: number) => {
    const enabled = items.map((it, k) => (it.disabled ? -1 : k)).filter((k) => k >= 0);
    if (!enabled.length) return;
    const target = enabled[(i + enabled.length) % enabled.length];
    itemRefs.current[target]?.focus();
  };

  const openMenu = (first: 'first' | 'last' = 'first') => {
    setOpen(true);
    requestAnimationFrame(() => focusItem(first === 'first' ? 0 : -1));
  };

  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) button.current?.focus();
  };

  const onButtonKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openMenu('first');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      openMenu('last');
    }
  };

  const onListKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const current = itemRefs.current.findIndex((el) => el === document.activeElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusItem(current + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusItem(current - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusItem(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusItem(-1);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === 'Tab') {
      close(false);
    }
  };

  return (
    <div className="relative inline-flex">
      <button
        ref={button}
        type="button"
        className={className}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-label={showLabel ? undefined : label}
        title={label}
        onClick={() => (open ? close(false) : openMenu())}
        onKeyDown={onButtonKey}
        data-testid={testId}
      >
        {children ?? (
          <>
            <Icon name={icon} size={14} />
            {showLabel && <span>{label}</span>}
          </>
        )}
      </button>
      {open && (
        <div
          ref={list}
          id={id}
          role="menu"
          aria-label={label}
          onKeyDown={onListKey}
          className={`fl-fade-in absolute top-full z-40 mt-2 min-w-[14rem] rounded-card border border-line bg-surface p-1 shadow-soft ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {items.map((item, i) => (
            <button
              key={item.id}
              ref={(el) => {
                itemRefs.current[i] = el;
              }}
              type="button"
              role={item.checked === undefined ? 'menuitem' : 'menuitemcheckbox'}
              aria-checked={item.checked}
              disabled={item.disabled}
              tabIndex={-1}
              data-testid={item.testId}
              onClick={() => {
                close();
                item.onSelect();
              }}
              className="flex w-full items-start gap-2 rounded-control px-2 py-2 text-left text-sm text-fg hover:bg-fg/[0.05] focus:bg-fg/[0.06] focus:outline-none disabled:opacity-50"
            >
              <span className="mt-0.5 flex w-4 shrink-0 justify-center text-muted">
                {item.checked !== undefined ? (item.checked ? <Icon name="check" size={13} /> : null) : item.icon ? <Icon name={item.icon} size={13} /> : null}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block">{item.label}</span>
                {item.hint && <span className="block text-2xs text-subtle">{item.hint}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
